<template>
  <div v-if="isDevEnv">
    <!-- FAB trigger -->
    <v-btn
      fab
      fixed
      bottom
      left
      small
      color="orange darken-2"
      style="z-index:9999; bottom:16px; left:16px;"
      @click="open = true"
      title="Store Debug"
    >
      <v-icon>mdi-bug</v-icon>
    </v-btn>

    <!-- Debug modal -->
    <v-dialog v-model="open" max-width="900" scrollable>
      <v-card>
        <v-card-title class="headline orange darken-2 white--text py-2">
          <v-icon color="white" class="mr-2">mdi-bug</v-icon>
          Store Debug Panel
          <v-spacer />
          <v-btn icon @click="open = false"><v-icon color="white">mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-tabs v-model="tab" background-color="grey darken-3" dark>
            <v-tab>Settings</v-tab>
            <v-tab>Cart Settings</v-tab>
            <v-tab>Bonus Rules ({{ bonusRules.length }})</v-tab>
            <v-tab>Rules/Templates</v-tab>
            <v-tab>Skin Rules Raw</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">

            <!-- ── TAB 0: Settings ───────────────────────────────────── -->
            <v-tab-item>
              <v-simple-table dense class="debug-table">
                <thead>
                  <tr><th>Key</th><th>Value</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(val, key) in settingsSnapshot" :key="key">
                    <td class="font-weight-bold">{{ key }}</td>
                    <td>
                      <span :class="valueClass(val)">{{ formatVal(val) }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>

            <!-- ── TAB 1: Cart Settings ──────────────────────────────── -->
            <v-tab-item>
              <div v-if="Object.keys(cartSettings).length === 0" class="pa-4 grey--text">
                Nessun cart setting trovato nello store.
              </div>
              <v-simple-table v-else dense class="debug-table">
                <thead>
                  <tr><th>Key</th><th>Value</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(val, key) in cartSettings" :key="key">
                    <td class="font-weight-bold">{{ key }}</td>
                    <td>
                      <span :class="valueClass(val)">{{ formatVal(val) }}</span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>

            <!-- ── TAB 2: Bonus Rules ────────────────────────────────── -->
            <v-tab-item>
              <div v-if="bonusRules.length === 0" class="pa-4 grey--text">
                Nessuna bonus rule trovata.
              </div>
              <v-simple-table v-else dense class="debug-table">
                <thead>
                  <tr>
                    <th>Multiplicity</th>
                    <th>Percentage %</th>
                    <th>Min Odd</th>
                    <th>Context</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rule, i) in bonusSorted" :key="i">
                    <td>{{ rule.multiplicity }}</td>
                    <td>
                      <v-chip x-small :color="rule.percentage > 0 ? 'green' : 'grey'" dark>
                        {{ rule.percentage }}%
                      </v-chip>
                    </td>
                    <td>{{ rule.minOddValue }}</td>
                    <td>{{ rule.context }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-tab-item>

            <!-- ── TAB 3: Rules / Templates ──────────────────────────── -->
            <v-tab-item>
              <v-expansion-panels accordion flat>
                <v-expansion-panel v-for="section in ruleSections" :key="section.label">
                  <v-expansion-panel-header>
                    <span>
                      {{ section.label }}
                      <v-chip x-small class="ml-2">{{ section.items.length }}</v-chip>
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="section.items.length === 0" class="grey--text caption pa-2">
                      Vuoto
                    </div>
                    <pre v-else class="debug-json">{{ JSON.stringify(section.items, null, 2) }}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-tab-item>

            <!-- ── TAB 4: Skin Rules Raw ─────────────────────────────── -->
            <v-tab-item>
              <pre class="debug-json pa-4">{{ JSON.stringify(skinRules, null, 2) }}</pre>
            </v-tab-item>

          </v-tabs-items>
        </v-card-text>

        <v-card-actions class="grey darken-3">
          <span class="caption grey--text">NODE_ENV: {{ nodeEnv }}</span>
          <v-spacer />
          <v-btn small text color="orange" @click="copyAll">
            <v-icon left small>mdi-content-copy</v-icon>Copy all
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'StoreDebugPanel',
  data: () => ({
    open: false,
    tab: 0,
  }),
  computed: {
    ...mapGetters({
      oddsType:                   'odds/getOddsType',
      gameConsole:                'settings/getGameConsole',
      shortCode:                  'settings/getShortCode',
      showQuickbet:               'settings/getShowQuickbet',
      nextevent:                  'settings/getNextEvent',
      eventday:                   'settings/getEventDay',
      cashout:                    'settings/getCashout',
      cashoutlive:                'settings/getCashoutLive',
      voidAsLose:                 'settings/getVoidAsLose',
      streamLive:                 'settings/getStreamLive',
      enableClearCart:            'settings/getEnableClearCartAtPlaceBet',
      enabledTaxOnWon:            'settings/getEnabledTaxOnWon',
      percentageTaxOnWon:         'settings/getPercentageTaxValueOnWon',
      enableAgencyCanPlay:        'settings/getEnableAgencyCanPlayForUser',
      enableMobileBottomToolbar:  'settings/getEnableMobileBottomToolbar',
      explodeMarketMobile:        'settings/getExplodeMarketOnMobileDevice',
      showMultiplicityMobile:     'settings/getShowMultiplicityLegendMobile',
      enableTreePlayerOutcomes:   'settings/getEnableTreeWithPlayerOutcomes',
      quickBetEnabled:            'settings/quickBetEnabled',
      allowIntegralLiveBet:       'settings/getAllowIntegralLiveBet',
      proxyUrl:                   'settings/getProxyUrl',
      platformSettings:           'settings/getPlatformSettings',
      results:                    'settings/getResults',
      featuredTournamentAutoLoad: 'settings/getFeaturedTournamentAutoLoad',
      featuredTournamentWidget:   'settings/getFeaturedTournamentWidget',
      icon:                       'settings/getIcon',
      electron:                   'settings/getElectron',
      rulesManager:               'rules/getRulesManager',
      rulesSkin:                  'rules/getRulesSkin',
      rulesDisabled:              'rules/getRulesDisabled',
      templateManager:            'rules/getTemplateManager',
      templateSkin:               'rules/getTemplateSkin',
      rulesCashoutManager:        'rules/getRulesCashoutManager',
      rulesCashoutSkin:           'rules/getRulesCashoutSkin',
    }),
    ...mapState({
      cartSettings:  state => state.cart.settings,
      bonusRules:    state => state.cart.bonus_rules,
    }),
    isDevEnv() {
      return process.env.NODE_ENV === 'local' || process.env.NODE_ENV === 'development';
    },
    nodeEnv() {
      return process.env.NODE_ENV;
    },
    settingsSnapshot() {
      return {
        'odds/oddsType':                this.oddsType,
        'settings/gameConsole':         this.gameConsole,
        'settings/shortCode':           this.shortCode,
        'settings/showQuickbet':        this.showQuickbet,
        'settings/nextevent':           this.nextevent,
        'settings/eventday':            this.eventday,
        'settings/cashout':             this.cashout,
        'settings/cashoutlive':         this.cashoutlive,
        'settings/voidAsLose':          this.voidAsLose,
        'settings/streamLive':          this.streamLive,
        'settings/enableClearCart':     this.enableClearCart,
        'settings/enabledTaxOnWon':     this.enabledTaxOnWon,
        'settings/percentageTaxOnWon':  this.percentageTaxOnWon,
        'settings/enableAgencyCanPlay': this.enableAgencyCanPlay,
        'settings/enableMobileToolbar': this.enableMobileBottomToolbar,
        'settings/explodeMarketMobile': this.explodeMarketMobile,
        'settings/showMultiMobile':     this.showMultiplicityMobile,
        'settings/treePlayerOutcomes':  this.enableTreePlayerOutcomes,
        'settings/quickBetEnabled':     this.quickBetEnabled,
        'settings/allowIntegralLive':   this.allowIntegralLiveBet,
        'settings/proxyUrl':            this.proxyUrl,
        'settings/icon':                this.icon,
        'settings/electron':            this.electron,
        'settings/results':             JSON.stringify(this.results),
        'settings/featuredAutoLoad':    this.featuredTournamentAutoLoad,
        'settings/featuredWidget':      this.featuredTournamentWidget,
        'settings/platformSettings':    JSON.stringify(this.platformSettings),
      };
    },
    bonusSorted() {
      return [...this.bonusRules].sort((a, b) => +a.multiplicity - +b.multiplicity);
    },
    skinRules() {
      return this.rulesSkin;
    },
    ruleSections() {
      return [
        { label: 'rules/rulesManager',       items: this.rulesManager       ?? [] },
        { label: 'rules/rulesSkin',           items: this.rulesSkin          ?? [] },
        { label: 'rules/rulesDisabled',       items: this.rulesDisabled      ?? [] },
        { label: 'rules/templateManager',     items: this.templateManager    ?? [] },
        { label: 'rules/templateSkin',        items: this.templateSkin       ?? [] },
        { label: 'rules/cashoutManager',      items: this.rulesCashoutManager ?? [] },
        { label: 'rules/cashoutSkin',         items: this.rulesCashoutSkin   ?? [] },
      ];
    },
  },
  methods: {
    formatVal(val) {
      if (val === null || val === undefined) return '—';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    },
    valueClass(val) {
      if (val === true)  return 'green--text font-weight-bold';
      if (val === false) return 'red--text';
      if (val === null || val === undefined) return 'grey--text';
      return '';
    },
    copyAll() {
      const data = {
        settings:     this.settingsSnapshot,
        cartSettings: this.cartSettings,
        bonusRules:   this.bonusSorted,
        ruleSections: Object.fromEntries(this.ruleSections.map(s => [s.label, s.items])),
      };
      navigator.clipboard.writeText(JSON.stringify(data, null, 2))
        .then(() => this.$store.dispatch('settings/setSnackbarText', 'Store data copied!'))
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.debug-table td,
.debug-table th {
  font-size: var(--font-size-2xl) !important;
  padding: 4px 8px !important;
}
.debug-json {
  font-size: var(--font-size-xl);
  white-space: pre-wrap;
  word-break: break-all;
  background: #1e1e1e;
  color: #d4d4d4;
  margin: 0;
  max-height: 400px;
  overflow-y: auto;
}
</style>
